/* eslint-disable formatjs/no-emoji */
/* eslint-disable formatjs/enforce-placeholders */
import Fundamentei from "components/branding/Fundamentei";
import IsolatedLayout from "components/layouts/IsolatedLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import { Separator } from "components/ui/Separator";
import Twemoji from "components/ui/Twemoji";
import aspectRatio from "components/utils/aspectRatio";
import { useAmplitude } from "lib/amplitude/Amplitude";
import { FundamentalProductOriginCodes } from "lib/analytics/FundamenteiProductCodes";
import trackEvent from "lib/analytics/trackEvent";
import { AwardIcon, CheckIcon, RocketIcon, SearchIcon } from "lucide-react";
import Link from "next/link";
import type { SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffectOnce } from "react-use";

export default function ScreenerLandingPage() {
  const intl = useIntl();
  const isEnglish = !intl.locale.startsWith("pt");

  const amplitude = useAmplitude();
  useEffectOnce(() => {
    amplitude.logEvent("Viewed Screener landing page", {
      origin: FundamentalProductOriginCodes.SCREENER,
    });
    trackEvent("Viewed Screener landing page", {
      origin: FundamentalProductOriginCodes.SCREENER,
    });
  });

  return (
    <IsolatedLayout forcedTheme="dark">
      {false && (
        <FundamenteiMetadata
          title={intl.formatMessage({ defaultMessage: "Busca de Ações | Fundamentei", id: "WqjSQ1" })}
          description={intl.formatMessage({
            defaultMessage:
              "Pesquise e compare milhares de ativos e indicadores em um único lugar, de maneira simples e unificada. Ações, FIIs, Stocks ou REITs. Governança, valuation e indicadores fundamentalistas",
            id: "3FGcJN",
          })}
        />
      )}

      <FundamenteiMetadata
        title={intl.formatMessage({ defaultMessage: "Busca de Ações | Fundamentei", id: "WqjSQ1" })}
        description={intl.formatMessage({
          defaultMessage:
            "Busque pelas melhores Ações, FIIs, Stocks e REITs em uma única planilha. Filtre por Lucro, DY, P/L, ROE e vários outros indicadores.",
          id: "gixcl8",
        })}
      />
      <main
        className={classNames(
          "flex flex-col bg-slate-900 px-6 py-12 text-center",
          "sm:px-16 sm:py-24",
          "min-h-svh",
          "overflow-x-hidden",
        )}
      >
        <nav className="absolute right-4 top-4 lg:right-12 lg:top-12">
          <a
            href={isEnglish ? "/screener" : "/en-US/screener"}
            lang={isEnglish ? "en-US" : "pt-BR"}
            hrefLang={isEnglish ? "en-US" : "pt-BR"}
            className={classNames(
              "inline-flex items-center gap-x-2 rounded-full bg-slate-300/10 px-3 py-1.5 text-xs font-bold tracking-tight text-slate-300 sm:text-sm",
              "hover:bg-slate-700",
              "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400/40",
            )}
          >
            {isEnglish ? (
              <>
                <Twemoji emoji="🇧🇷" aria-hidden="true" width={18} height={18} />
                <FormattedMessage defaultMessage="Português (BR)" id="+4HKcw" />
              </>
            ) : (
              <>
                <Twemoji emoji="🇺🇸" aria-hidden="true" width={18} height={18} />
                <FormattedMessage defaultMessage="English (US)" id="04Aelp" />
              </>
            )}
          </a>
        </nav>

        <div>
          <a
            href="/"
            className={classNames(
              "inline-flex scale-75 items-center gap-x-2 rounded-3xl px-4 py-3 text-white",
              "hover:bg-slate-200/10 hover:text-white",
              "sm:scale-100",
              "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400/40",
            )}
          >
            <Fundamentei
              fill="currentColor"
              className="inline-flex"
              {...aspectRatio({
                originalWidth: 372,
                originalHeight: 392,
                desiredHeight: 60,
              })}
            />
            <ScreenerWordmark
              className="text-cyan-400"
              {...aspectRatio({
                originalWidth: 170,
                originalHeight: 38,
              })}
            />
          </a>
        </div>

        <Hero />
        <Separator className="my-8 lg:my-16" />
        <FeaturesGrid />
        <Separator className="my-8 lg:my-16" />
        <Filters />
        <Separator className="my-8 lg:my-16" />
        <KeyDifferentials />
        <Separator className="my-8 lg:my-16" />
        <LetterFromCEO />
        <Separator className="mx-auto my-8 max-w-7xl opacity-40 lg:my-16" />
        <StartScreening />

        <footer className="flex flex-col items-center gap-y-4 pt-16 text-center">
          <Link
            href="/"
            className={classNames(
              "inline-flex scale-75 items-center gap-x-2 rounded-full p-6 text-white",
              "hover:bg-slate-200/10 hover:text-white",
              "sm:scale-100",
              "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400/40",
            )}
          >
            <Fundamentei
              fill="currentColor"
              className="inline-flex"
              {...aspectRatio({
                originalWidth: 372,
                originalHeight: 392,
                desiredHeight: 60,
              })}
            />
          </Link>

          <FundamenteiSignature className="inline-flex" />

          {/* Copyright */}
          <p className={classNames("text-sm font-medium tracking-wider text-slate-300")}>
            © 2019 — {new Date().getFullYear()} Fundamentei
          </p>
        </footer>
      </main>
    </IsolatedLayout>
  );
}

function KeyDifferentials() {
  const intl = useIntl();
  return (
    <section className="mx-auto flex w-full max-w-7xl flex-col items-start gap-y-8">
      <header className="flex flex-col items-center md:items-start">
        <AwardIcon className="size-16 bg-teal-400/20 fill-transparent stroke-teal-300 p-4 squircle" />

        <h2 className="mt-4 text-4xl font-bold tracking-tighter text-white sm:text-5xl">
          <FormattedMessage defaultMessage="Diferenciais, são vários." id="iUWp62" />
        </h2>

        <p
          className={classNames(
            "mt-2 text-pretty text-base font-medium tracking-wide text-gray-300",
            "sm:text-lg",
            "[&>strong]:font-bold [&>strong]:dark:text-purple-400",
          )}
        >
          <FormattedMessage
            defaultMessage="Eis uma lista com os vários diferenciais que temos na nossa planilha/busca de ações. Confira você mesmo, e compare."
            id="lLWSeT"
          />
        </p>
      </header>

      <ul className="flex flex-col gap-6 sm:gap-3.5">
        {[
          intl.formatMessage({
            defaultMessage:
              "<b>Cores</b> — colorimos tudo. Sempre temos o cuidado de apresentar os dados de uma maneira que facilite a leitura/interpretação. Indo além do básico.",
            id: "Rq3oak",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Formatação</b> — igualmente importante é a formatação dos números (%, bilhões, L, casas decimais, etc). Fica tudo mais fácil para ler.",
            id: "JTVETe",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Documentação</b> — uma breve explicação de cada indicador, caso você tenha dúvidas do que se trata. É só passar o mouse na coluna e voilà.",
            id: "gHR2ZZ",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Dark mode nativo</b> — sem dúvidas, faz diferença. O nosso dark mode foi feito no detalhe, para preservar o que há de melhor no design.",
            id: "BzSipM",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Recursos da tabela</b> — bem parecido com o Excel™ de sempre. Você pode ordenar, reorganizar, remover colunas. Tudo muito simples.",
            id: "0ohbad",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Integração</b> — é possível filtrar pelos ativos da sua carteira, pelos seus favoritos, ou simplesmente por tudo disponível, basta um clique.",
            id: "G562W9",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Simplesmente todos os ativos</b> — fizemos questão de ter a maior cobertura de ativos possível: Ações, Stocks, REITs, FIIs e Fiagros.",
            id: "MGUGSM",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Muitos indicadores</b> — quase que ilimitados. Além dos tradicionais, temos vários outros que é difícil encontrar por aí.",
            id: "RXFBKN",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Performance</b> — cada segundo conta. Construído com tecnologia de ponta, buscamos manter a régua de performance abaixo de 1 segundo.",
            id: "nr9NMc",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Mobile</b> — sem restrições, a experiência é praticamente idêntica no celular. Desenvolvemos até um teclado customizado para os filtros.",
            id: "ltvhV5",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Paginação otimizada</b>. Já viu como é fácil e diferente navegar entre as páginas? Ou até mesmo voltar tudo?",
            id: "c+wnD5",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Fácil gestão de Screeners</b> — com apenas 2 botões, você guarda e organiza tudo. Fica bem fácil de acessar depois.",
            id: "RIqvWN",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Busca de indicadores</b>. Organizada, rápida e prática. Além de você poder favoritar os que mais usa.",
            id: "umX3CQ",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Atualização em tempo real</b>. Assim que um balanço é atualizado no nosso banco de dados, já atualiza no Screener.",
            id: "mQ7Awv",
          }),
          intl.formatMessage({
            defaultMessage:
              "<b>Sistema de filtros único</b>. Fizemos uma linguagem própria, desenvolvemos um teclado para facilitar no celular. Simplesmente único.",
            id: "MNeFkx",
          }),
        ].map((line) => {
          return (
            <li key={line} className="flex gap-x-2 text-left">
              <div>
                <div className="rounded-full bg-lime-100 p-1 text-lime-600 dark:bg-lime-600/20 dark:text-lime-300">
                  <CheckIcon className="size-3" strokeWidth={3} />
                </div>
              </div>
              <p
                className={classNames(
                  "font-medium tracking-wider text-stone-300",
                  "[&>strong]:font-bold [&>strong]:brightness-100 [&>strong]:dark:text-lime-200",
                )}
              >
                {line}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Hero() {
  const intl = useIntl();
  const isEnglish = !intl.locale.startsWith("pt");
  const baseScreenerImagesPath = isEnglish ? "/static/screenshots/screener/en" : "/static/screenshots/screener/pt";
  return (
    <section className="mx-auto max-w-7xl">
      <header className="mx-auto flex max-w-7xl flex-col items-center gap-y-4 pb-12 pt-8">
        <h1 className="max-w-6xl text-balance text-4xl font-bold tracking-tighter text-white sm:text-5xl">
          <FormattedMessage defaultMessage="Ações, FIIs, Stocks e REITs. Uma única planilha." id="NjQkE3" />
        </h1>

        <p
          className={classNames(
            "max-w-4xl text-pretty text-base font-medium tracking-wide text-gray-300",
            "sm:text-lg",
            "[&>strong]:font-bold [&>strong]:dark:text-cyan-300",
          )}
        >
          <FormattedMessage
            defaultMessage="Você está procurando as melhores ações para investir, mas se sente perdido em meio a tantos dados e informações? Nossa <b>planilha de ações</b> foi desenvolvida para simplificar sua <b>busca por ações</b> e otimizar suas decisões de investimento."
            id="2koVGL"
          />
        </p>
      </header>

      <div className="overflow-hidden rounded-xl border border-slate-950 lg:rounded-3xl">
        <img
          alt={intl.formatMessage({ defaultMessage: "Screener - Tela principal", id: "YFXhzV" })}
          src={`${baseScreenerImagesPath}/hero.jpg`}
          width={2884}
          height={2160}
          className={classNames(
            "flex aspect-square origin-top-left scale-[2] items-center justify-center bg-slate-300/5 object-cover object-left-top md:aspect-video md:object-top",
            "sm:origin-top-left sm:scale-125",
            "lg:max-w-5xl",
          )}
        />
      </div>

      <div className="mt-10 flex items-center justify-center gap-x-6">
        <Link
          shallow={true}
          href={{
            pathname: "/sign-up",
            query: { redirectTo: "/screener" },
          }}
          className={classNames(
            "border-transparent py-2 text-lg font-bold tracking-tight text-sky-400 brightness-125",
            "transition-all hover:scale-125 hover:border-orange-400 hover:text-orange-500 hover:brightness-150",
            "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-sky-400",
            "rounded-full bg-sky-300/10 p-5 sm:p-6",
          )}
        >
          <FormattedMessage defaultMessage="Cadastre-se já e acesse a planilha" id="vkrjeX" />
        </Link>
      </div>
    </section>
  );
}

function Filters() {
  const intl = useIntl();
  const isEnglish = !intl.locale.startsWith("pt");
  const baseScreenerImagesPath = isEnglish ? "/static/screenshots/screener/en" : "/static/screenshots/screener/pt";

  return (
    <section className="mx-auto max-w-7xl">
      <header className="flex flex-col items-center gap-y-2 pb-12">
        <SearchIcon className="size-16 bg-slate-500/20 p-4 squircle" />

        <h3 className="text-4xl font-bold tracking-tighter text-white">
          <FormattedMessage defaultMessage="Busca de ações" id="W4DRZi" />
        </h3>

        <p
          className={classNames(
            "max-w-5xl text-pretty text-base font-medium tracking-wide text-gray-300",
            "sm:text-lg",
            "[&>strong]:font-bold [&>strong]:dark:text-orange-400",
          )}
        >
          <FormattedMessage
            defaultMessage="Criamos um avançado sistema de busca para você encontrar as melhores Ações, FIIs, Stocks e REITs que estejam por aí. Busque, analise e compare as melhores ações em segundos."
            id="3tUgPR"
          />
        </p>
      </header>

      <div className="flex flex-col items-center gap-y-12">
        <div className="overflow-hidden rounded-xl border border-slate-950 lg:rounded-3xl">
          <img
            alt={intl.formatMessage({ defaultMessage: "Filtros", id: "cocT5g" })}
            src={`${baseScreenerImagesPath}/filters.jpg`}
            width={2884}
            height={2160}
            className={classNames(
              "flex aspect-square origin-top-left scale-[1.25] items-center justify-center bg-slate-300/5 object-cover object-left-top md:aspect-video md:object-top",
              "sm:origin-center sm:scale-100",
              "lg:max-w-5xl",
            )}
          />
        </div>

        <ul className="mx-auto grid grid-cols-1 gap-6 pt-8 md:grid-cols-3">
          <li className="flex flex-col items-center text-center">
            <img
              alt={intl.formatMessage({ defaultMessage: "Filtros múltiplos", id: "5sXo/E" })}
              src={`${baseScreenerImagesPath}/parallel-filters.jpg`}
              className="aspect-square size-80 rounded-2xl border border-slate-950 bg-slate-300/5 object-cover object-left-top lg:size-96"
              width={2160}
              height={2160}
            />
            <h3 className="mt-6 text-2xl font-bold tracking-tight dark:text-white">
              <FormattedMessage defaultMessage="Mais de um filtro ao mesmo tempo" id="nlYLVs" />
            </h3>
            <p className="mt-2 text-base font-medium tracking-wide dark:text-slate-300">
              <FormattedMessage
                defaultMessage="Em alguns casos, só um filtro não basta, é preciso aprofundar. Com poucos cliques, você pode adicionar vários filtros ao mesmo tempo."
                id="/OlWsB"
              />
            </p>
          </li>

          <li className="flex flex-col items-center text-center">
            <img
              alt={intl.formatMessage({ defaultMessage: "Filtros avançados", id: "JBeECV" })}
              src={`${baseScreenerImagesPath}/filter-between.jpg`}
              className="aspect-square size-80 rounded-2xl border border-slate-950 bg-slate-300/5 object-cover object-left-top lg:size-96"
              width={2160}
              height={2160}
            />
            <h3 className="mt-6 text-2xl font-bold tracking-tight brightness-125 dark:text-orange-500">
              <FormattedMessage defaultMessage="Filtros, pensados para praticidade" id="G4DHgF" />
            </h3>
            <p className="mt-2 text-base font-medium tracking-wide dark:text-slate-300">
              <FormattedMessage
                defaultMessage="Inventamos um sistema avançado de filtros, <b>mas sem a parte da complexidade</b>. Quase nenhum clique e muito poder, dá pra encontrar praticamente tudo."
                id="UVBZlt"
              />
            </p>
          </li>

          <li className="flex flex-col items-center text-center">
            <img
              alt={intl.formatMessage({ defaultMessage: "Indicadores customizados", id: "bXDf4B" })}
              src={`${baseScreenerImagesPath}/custom-indicators.jpg`}
              className="aspect-square size-80 rounded-2xl border border-slate-950 bg-slate-300/5 object-cover object-left-top lg:size-96"
              width={2160}
              height={2160}
            />
            <h3 className="mt-6 text-2xl font-bold tracking-tight dark:text-green-400">
              <FormattedMessage defaultMessage="Indicadores nunca antes vistos" id="1oMoBi" />
            </h3>
            <p className="mt-2 text-base font-medium tracking-wide dark:text-slate-300">
              <FormattedMessage
                defaultMessage="Do básico ao avançado, seja <b>valuation</b>, <b>payout</b>, <b>governança</b> ou <b>fundamentalistas</b>, nosso catalogo de indicadores é vasto."
                id="Xw2Q60"
              />
            </p>
          </li>
        </ul>

        <LogoCloud
          className="hidden opacity-40 lg:flex"
          {...aspectRatio({
            originalWidth: 2049,
            originalHeight: 87,
            desiredWidth: 960,
          })}
        />
      </div>
    </section>
  );
}

function FeaturesGrid() {
  const intl = useIntl();
  const isEnglish = !intl.locale.startsWith("pt");
  const baseScreenerImagesPath = isEnglish ? "/static/screenshots/screener/en" : "/static/screenshots/screener/pt";

  return (
    <section className="mx-auto flex max-w-7xl flex-col gap-y-4">
      <header className="flex flex-col items-center md:items-start md:text-left">
        <RocketIcon className="size-16 bg-slate-500/20 p-4 squircle" />

        <h2 className="mt-4 text-4xl font-bold tracking-tighter text-white sm:text-5xl">
          <FormattedMessage defaultMessage="Recursos diferenciados." id="5g0Xpd" />
        </h2>

        <p
          className={classNames(
            "mt-2 text-pretty text-base font-medium tracking-wide text-gray-300",
            "sm:text-lg",
            "[&>strong]:font-bold [&>strong]:dark:text-orange-400",
          )}
        >
          <FormattedMessage
            defaultMessage="Cada detalhe foi minimamente pensado e desenvolvido para proporcionar uma imersão completa nos ativos, nos dados, <b>no que importa.</b>"
            id="fwG8v9"
          />
        </p>
      </header>

      <ul className="mx-auto grid grid-cols-1 gap-6 pt-8 md:grid-cols-3">
        <li className="flex flex-col items-center text-center md:items-start md:text-left">
          <img
            alt={intl.formatMessage({ defaultMessage: "Todos indicadores", id: "QUJjgu" })}
            src={`${baseScreenerImagesPath}/indicators-select.jpg`}
            className="aspect-square size-80 rounded-2xl border border-slate-950 bg-slate-300/5 object-cover object-left-top lg:size-96"
            width={2160}
            height={2160}
          />
          <h3 className="mt-6 text-2xl font-bold tracking-tight dark:text-lime-300">
            <FormattedMessage defaultMessage="Todos os indicadores. Todos." id="+bxEvU" />
          </h3>
          <p className="mt-2 text-base font-medium tracking-wide dark:text-slate-300">
            <FormattedMessage
              defaultMessage="Já são mais de 900 no total, e contando. Você pode pesquisar, favoritar e adicionar vários ao mesmo tempo na tabela, conforme preferir."
              id="b18/4h"
            />
          </p>
        </li>

        <li className="flex flex-col items-center text-center md:items-start md:text-left">
          <img
            alt={intl.formatMessage({ defaultMessage: "Lista", id: "gVJL1l" })}
            src={`${baseScreenerImagesPath}/ticker-symbol-list-select.jpg`}
            className="aspect-square size-80 rounded-2xl border border-slate-950 bg-slate-300/5 object-cover object-left-top lg:size-96"
            width={2160}
            height={2160}
          />
          <h3 className="mt-6 text-2xl font-bold tracking-tight brightness-150 dark:text-orange-400">
            <FormattedMessage defaultMessage="Carteira, favoritos ou todos os ativos" id="tsxX/i" />
          </h3>
          <p className="mt-2 text-base font-medium tracking-wide dark:text-slate-300">
            <FormattedMessage
              defaultMessage="Uma forma simples de analisar e comparar Ações, FIIs, Stocks ou REITs—sejam eles da sua <b>carteira</b>, <b>favoritos</b> ou <b>todos os ativos</b> disponíveis na plataforma."
              id="7qfooH"
            />
          </p>
        </li>

        <li className="flex flex-col items-center text-center md:items-start md:text-left">
          <img
            alt={intl.formatMessage({ defaultMessage: "Screeners salvos", id: "qv+B7q" })}
            src={`${baseScreenerImagesPath}/saved-screeners.jpg`}
            className="aspect-square size-80 rounded-2xl border border-slate-950 bg-slate-300/5 object-cover object-left-top lg:size-96"
            width={2160}
            height={2160}
          />
          <h3 className="mt-6 text-2xl font-bold tracking-tight dark:text-white">
            <FormattedMessage defaultMessage="Não perca nada" id="IpxIIa" />
          </h3>
          <p className="mt-2 text-base font-medium tracking-wide dark:text-slate-300">
            <FormattedMessage
              defaultMessage="Salve e organize suas planilhas e tenha acesso a elas sempre que você precisar. Rápido e fácil, simples e objetivo."
              id="8RDBTf"
            />
          </p>
        </li>
      </ul>
    </section>
  );
}

function LetterFromCEO() {
  return (
    <section className="mx-auto max-w-7xl pt-12 text-left">
      <blockquote className="flex flex-col gap-y-4 border-slate-600 text-lg font-medium tracking-wide text-slate-300 md:text-xl">
        <p>
          <FormattedMessage
            defaultMessage={`"Nosso planilhão de ações é simplesmente o melhor produto que já fizemos até hoje, em 6 anos de empresa. Sempre fui exigente com usabilidade e já fizemos outras ferramentas excepcionais, mas nada comparado ao planilhão. Pessoalmente, eu nunca vi nada parecido em outro site. A imensidão de dados, de indicadores, um design impecável e tudo muito rápido, permite com que eu compare milhares de ativos em instantes. Fico sem palavras para expressar o quão bom ele é — use você mesmo e me diga."`}
            id="z7tCiX"
          />
        </p>
        <time dateTime="2025-05" className="flex items-center text-sm/6 font-semibold text-orange-400 brightness-125">
          <FormattedMessage defaultMessage="— Maio, 2025" id="8wYt4a" />
          <div
            className="absolute -ml-2 h-px w-screen -translate-x-full bg-gray-900/10 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
            aria-hidden="true"
          />
        </time>
      </blockquote>
      <figcaption className="mt-8 flex items-center gap-x-4">
        <div className="size-20 overflow-hidden rounded-full">
          <img
            src="/static/people/v2/eduardo.png"
            alt="Eduardo Cavalcanti"
            className="aspect-square object-cover squircle"
          />
        </div>
        <div>
          <div className="text-lg font-bold tracking-tight text-white">Eduardo Cavalcanti</div>
          <div className="text-base font-medium tracking-tighter text-slate-300">CEO, Fundamentei</div>
        </div>
      </figcaption>
    </section>
  );
}

function StartScreening() {
  return (
    <section className="mx-auto max-w-7xl pt-12">
      <div className="relative isolate overflow-hidden rounded-3xl py-2 text-center">
        <h3 className="text-balance text-4xl font-bold tracking-tighter text-sky-500 brightness-150 sm:text-5xl">
          <FormattedMessage defaultMessage="Pronto para começar?" id="wg6D8T" />
        </h3>
        <p className="mx-auto mt-6 max-w-3xl text-pretty text-lg font-medium text-slate-200">
          <FormattedMessage
            defaultMessage="Sem mais delongas, cadastre-se agora mesmo e começe uma nova jornada de comparação e pesquisa de ativos. Deixe para trás o tempo perdido com planilhas e relatórios."
            id="fbYPZq"
          />
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <a
            href="/sign-up"
            className={classNames(
              "rounded-full bg-sky-600/20 p-4 px-12 text-xl font-semibold text-sky-200 shadow-sm",
              "transition-all hover:scale-105 hover:bg-sky-600/40 hover:text-white",
              "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400",
            )}
          >
            <FormattedMessage defaultMessage="Cadastre-se já" description="Screener landing page" id="FxBPci" />
          </a>
        </div>
      </div>
    </section>
  );
}

function ScreenerWordmark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={170} height={38} {...props}>
      <title>Screener</title>
      <g fill="currentColor" fillRule="nonzero">
        <path d="m22.797 6.203-4.43 2.625C17.54 7.391 16.75 6.453 16 6.016c-.781-.5-1.79-.75-3.023-.75-1.516 0-2.774.43-3.774 1.289-1 .843-1.5 1.906-1.5 3.187 0 1.766 1.313 3.188 3.938 4.266l3.609 1.476c2.937 1.188 5.086 2.637 6.445 4.348 1.36 1.711 2.04 3.809 2.04 6.293 0 3.328-1.11 6.078-3.329 8.25-2.234 2.188-5.008 3.281-8.32 3.281-3.14 0-5.734-.93-7.781-2.789-2.016-1.86-3.274-4.476-3.774-7.851l5.531-1.22c.25 2.126.688 3.595 1.313 4.407 1.125 1.563 2.766 2.344 4.922 2.344 1.703 0 3.117-.57 4.242-1.711 1.125-1.14 1.688-2.586 1.688-4.336 0-.703-.098-1.348-.293-1.934a4.906 4.906 0 0 0-.915-1.617c-.414-.492-.949-.953-1.605-1.383-.656-.43-1.437-.84-2.344-1.23l-3.492-1.453c-4.953-2.094-7.43-5.156-7.43-9.188 0-2.718 1.04-4.992 3.118-6.82C7.344 1.031 9.93.109 13.023.109c4.172 0 7.43 2.032 9.774 6.094ZM44.078 15.156v7.008c-1.203-1.469-2.281-2.476-3.234-3.023-.938-.563-2.04-.844-3.305-.844-1.984 0-3.633.695-4.945 2.086-1.313 1.39-1.969 3.133-1.969 5.226 0 2.141.633 3.899 1.898 5.274 1.282 1.375 2.914 2.062 4.899 2.062 1.265 0 2.383-.273 3.351-.82.938-.531 2.04-1.562 3.305-3.094v6.961c-2.14 1.11-4.281 1.664-6.422 1.664-3.531 0-6.484-1.14-8.86-3.422-2.374-2.296-3.562-5.148-3.562-8.554 0-3.407 1.203-6.282 3.61-8.625 2.406-2.344 5.36-3.516 8.86-3.516 2.25 0 4.374.54 6.374 1.617ZM46.187 14.172h5.274v2.039c.969-1.016 1.828-1.711 2.578-2.086.766-.39 1.672-.586 2.719-.586 1.39 0 2.844.453 4.36 1.36l-2.415 4.828c-1-.72-1.976-1.079-2.93-1.079-2.875 0-4.312 2.172-4.312 6.516V37h-5.274V14.172ZM81.602 26.828h-16.36c.14 1.875.75 3.367 1.828 4.477 1.078 1.093 2.461 1.64 4.149 1.64 1.312 0 2.398-.312 3.258-.937.843-.625 1.804-1.781 2.882-3.469l4.453 2.484c-.687 1.172-1.414 2.176-2.18 3.012a11.59 11.59 0 0 1-2.46 2.063c-.875.539-1.82.933-2.836 1.183-1.016.25-2.117.375-3.305.375-3.406 0-6.14-1.093-8.203-3.281-2.062-2.203-3.094-5.125-3.094-8.766 0-3.609 1-6.53 3-8.765 2.016-2.203 4.688-3.305 8.016-3.305 3.36 0 6.016 1.07 7.969 3.211 1.937 2.125 2.906 5.07 2.906 8.836l-.023 1.242Zm-5.415-4.312c-.734-2.813-2.507-4.22-5.32-4.22-.64 0-1.242.099-1.805.294a5.163 5.163 0 0 0-1.535.844c-.46.367-.855.808-1.183 1.324a6.154 6.154 0 0 0-.75 1.758h10.593ZM105.156 26.828h-16.36c.141 1.875.75 3.367 1.829 4.477 1.078 1.093 2.46 1.64 4.148 1.64 1.313 0 2.399-.312 3.258-.937.844-.625 1.805-1.781 2.883-3.469l4.453 2.484c-.687 1.172-1.414 2.176-2.18 3.012a11.59 11.59 0 0 1-2.46 2.063c-.875.539-1.82.933-2.836 1.183-1.016.25-2.118.375-3.305.375-3.406 0-6.14-1.093-8.203-3.281-2.063-2.203-3.094-5.125-3.094-8.766 0-3.609 1-6.53 3-8.765 2.016-2.203 4.688-3.305 8.016-3.305 3.359 0 6.015 1.07 7.968 3.211 1.938 2.125 2.907 5.07 2.907 8.836l-.024 1.242Zm-5.414-4.312c-.734-2.813-2.508-4.22-5.32-4.22-.64 0-1.242.099-1.805.294a5.163 5.163 0 0 0-1.535.844c-.461.367-.855.808-1.184 1.324a6.154 6.154 0 0 0-.75 1.758h10.594ZM107.922 14.172h5.297v2.11c1.843-1.829 3.922-2.743 6.234-2.743 2.656 0 4.727.836 6.211 2.508 1.281 1.422 1.922 3.742 1.922 6.96V37h-5.297V24.25c0-2.25-.312-3.805-.937-4.664-.61-.875-1.72-1.313-3.329-1.313-1.75 0-2.992.579-3.726 1.735-.719 1.14-1.078 3.133-1.078 5.976V37h-5.297V14.172ZM152.031 26.828h-16.36c.141 1.875.75 3.367 1.829 4.477 1.078 1.093 2.46 1.64 4.148 1.64 1.313 0 2.399-.312 3.258-.937.844-.625 1.805-1.781 2.883-3.469l4.453 2.484c-.687 1.172-1.414 2.176-2.18 3.012a11.59 11.59 0 0 1-2.46 2.063c-.875.539-1.82.933-2.836 1.183-1.016.25-2.118.375-3.305.375-3.406 0-6.14-1.093-8.203-3.281-2.063-2.203-3.094-5.125-3.094-8.766 0-3.609 1-6.53 3-8.765 2.016-2.203 4.688-3.305 8.016-3.305 3.359 0 6.015 1.07 7.968 3.211 1.938 2.125 2.907 5.07 2.907 8.836l-.024 1.242Zm-5.414-4.312c-.734-2.813-2.508-4.22-5.32-4.22-.64 0-1.242.099-1.805.294a5.163 5.163 0 0 0-1.535.844c-.461.367-.855.808-1.184 1.324a6.154 6.154 0 0 0-.75 1.758h10.594ZM154.797 14.172h5.273v2.039c.969-1.016 1.828-1.711 2.578-2.086.766-.39 1.672-.586 2.72-.586 1.39 0 2.843.453 4.359 1.36l-2.415 4.828c-1-.72-1.976-1.079-2.93-1.079-2.874 0-4.312 2.172-4.312 6.516V37h-5.273V14.172Z" />
      </g>
    </svg>
  );
}

function LogoCloud(props: SVGProps<SVGSVGElement>) {
  const intl = useIntl();
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={2049} height={87} {...props} viewBox="0 0 2049 87">
      <title>
        {intl.formatMessage({
          defaultMessage: "Logo das empresas",
          id: "Kr/4+s",
        })}
      </title>
      <defs>
        <linearGradient id="a" x1="18.606%" x2="121.689%" y1="17.344%" y2="135.744%">
          <stop offset="0%" stopColor="#1A1E21" />
          <stop offset="100%" stopColor="#06060A" />
        </linearGradient>
      </defs>
      <g fill="none" fillRule="evenodd">
        <g fillRule="nonzero" transform="translate(196.5)">
          <rect width={86} height={86} fill="#F4F4F5" rx={43} />
          <path
            fill="#F37435"
            d="M68.13 63.631c-.417 2.284-2.57 4.125-4.779 4.526-13.91 2.521-27.238 2.54-41.143 0-2.285-.418-4.126-2.572-4.526-4.781-2.522-13.908-2.54-27.239 0-41.143.417-2.286 2.572-4.125 4.78-4.526 13.908-2.521 27.239-2.541 41.143 0 2.286.418 4.127 2.572 4.526 4.781 2.523 13.908 2.541 27.239 0 41.143Z"
          />
          <path
            fill="#FFF"
            d="M55.133 41.584h6.465l.022-3.285-6.465 1.129zM21.994 56.956h3.982v-13.44h-3.982zM23.985 37.303c1.293 0 2.342 1.025 2.342 2.29 0 1.268-1.05 2.293-2.342 2.293-1.293 0-2.342-1.025-2.342-2.292 0-1.266 1.05-2.291 2.342-2.291ZM29.518 39.998v3.482H27.55v2.995h1.968l.006 5.883c.014 5.947 5.293 5.059 7.096 4.526V54.01c-2.12.468-3.324-.132-3.324-2.112v-5.424h3.49V43.48h-3.49v-4.074l-3.778.592ZM44.18 43.169c2.771.088 5.878 1.325 5.878 4.93v8.974h-3.723v-1.45c-.676.948-2.16 1.672-3.88 1.646-2.224-.033-4.238-1.385-4.28-4.124-.047-3.08 2.648-4.68 8.052-4.584.093-1.946-1.585-2.392-3.28-2.323-1.38.058-3.127.43-4.058.824l-.018-2.898c1.18-.7 3.228-1.061 5.309-.995Zm2.188 7.713c-3.784-.02-4.307 1.004-4.368 1.978l-.003.06c-.044 1.008.97 1.756 2.039 1.605 1.074-.15 2.332-.595 2.332-2.294v-1.349ZM60.43 56.956h3.633v-13.44h-3.908v8.407c0 1.352-.986 2.23-2.21 2.264-1.211.034-1.954-.748-1.954-2.416v-8.255h-3.83v9.3c0 3.127 2.406 4.42 4.646 4.41 1.367-.007 2.52-.637 3.622-1.753v1.483Z"
          />
        </g>
        <path
          fill="#00448C"
          fillRule="nonzero"
          d="M1023.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
        />
        <path
          fill="#FFF"
          d="M1021.111 66.889c4.42 0 8.528-1.481 11.945-4.023v4.023h14.333V19.11h-14.333v4.023a19.919 19.919 0 0 0-11.945-4.023c-11.873 0-21.5 10.698-21.5 23.889 0 13.191 9.627 23.889 21.5 23.889Zm2.389-9.556c5.28 0 9.556-6.416 9.556-14.333s-4.277-14.333-9.556-14.333c-5.28 0-9.556 6.416-9.556 14.333s4.277 14.333 9.556 14.333Z"
        />
        <g fillRule="nonzero">
          <path
            fill="#EA1D25"
            d="M1612.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
          />
          <path
            fill="#FFF"
            d="M1622.968 36.32a11.228 11.228 0 0 0-1.414-4.95l-7.42-13.692a12.16 12.16 0 0 1-1.194-3.345l-.335.574a11.448 11.448 0 0 0 0 10.94l6.001 10.937c1.854 3.34 1.854 7.597 0 10.936l-.325.574a12.145 12.145 0 0 0-1.2-3.335l-5.455-10.02-3.488-6.45a12.16 12.16 0 0 1-1.204-3.334l-.325.573a11.448 11.448 0 0 0 0 10.941l6 10.937c1.855 3.34 1.855 7.596 0 10.936l-.329.573a12.16 12.16 0 0 0-1.199-3.334l-7.525-13.703a12.7 12.7 0 0 1-1.419-5.987c-7.965 2.189-13.526 6.909-13.526 12.322 0 7.597 10.688 13.698 23.889 13.698 13.196 0 23.889-6.101 23.889-13.698.11-5.184-5.346-9.904-13.42-12.088v-.004Z"
          />
        </g>
        <g fillRule="nonzero" transform="translate(784.5)">
          <rect width={86} height={86} fill="#194274" rx={43} />
          <g fill="#FFF">
            <path d="M60.816 26.15c-.185 0-.369-.185-.553-.185-4.975-1.107-12.162.37-18.98 6.46l1.843 2.398c5.712-5.536 13.083-9.042 18.058-8.489.185 0 .369-.184-.184-.184m-3.501-2.953c.368.185.368 0-.184-.369-.185 0-.369-.184-.553-.184a23.005 23.005 0 0 0-22.113 1.291l1.843 2.4c6.081-4.245 14.742-5.537 21.007-2.953M35.755 29.84l1.843 2.399c7.555-7.566 17.137-9.227 22.665-7.013.184 0 .368-.184-.369-.37-.184-.183-.553-.183-.737-.368a21.722 21.722 0 0 0-23.402 5.352M50.312 51.8l-1.842-2.4c-5.713 5.537-13.083 9.043-18.059 8.49-.184 0-.184.184.369.184.184 0 .368 0 .553.185 4.975 1.107 12.161-.37 18.98-6.46m-16.216 9.228c-.369-.185-.369 0 .184.369.184 0 .368.184.553.184a23.005 23.005 0 0 0 22.112-1.292l-1.843-2.399c-6.08 4.245-14.741 5.537-21.006 2.953m21.56-6.643-1.844-2.4c-7.555 7.567-17.137 9.412-22.665 7.013-.184 0-.368.185.369.37.184.184.553.184.737.368A21.721 21.721 0 0 0 55.656 54.2" />
            <path d="M66.012 52.335A23.246 23.246 0 0 1 55.504 62.86c-11.51 5.764-25.506 1.146-31.341-10.34-5.38-11.677-.363-25.515 11.246-31.015a23.018 23.018 0 0 1 9.77-2.4c1.36.009 2.717.132 4.056.369.37 0 .37-.185 0-.37a25.593 25.593 0 0 0-4.055-.738c-10.362-.7-20.054 5.164-24.255 14.675a24.765 24.765 0 0 0 5.473 27.844 24.677 24.677 0 0 0 28 4.375 25.094 25.094 0 0 0 12.167-12.924c.376-.819.624-1.69.737-2.585 0-.923-.737 1.477-1.29 2.585Z" />
          </g>
        </g>
        <path
          fill="#00939A"
          fillRule="nonzero"
          d="M1907.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
        />
        <path
          fill="#ECB833"
          d="M1930.5 40.133c-7.616 5.046-14.658 4.501-21.141-1.624 13.138-4.443 20.061-11.3 26.803-5.418l-4.998 6.307h-.01v.014h-.009v.01h-.019c-.005 0 0 .009-.01.009v.01h-.004l-.005.01c-.014 0-.02.013-.024.013v.005l-.005.01-.01.005c0 .01-.014.023-.023.023v.02h-.005v.004l-.005.005h-.01v.014h-.004l-.014.01h-.005a.702.702 0 0 0-.067.076v.005l-.01.02v.004c-.01 0-.028.024-.038.024l-.057.033h-.01l-.004.01h-.01v.005l-.014.01h-.005v.004a.158.158 0 0 1-.034.02l-.01.014-.009.01h-.004c0 .119-.187.162-.187.238 0 .005-.024.024-.024.033l-.014.015c-.014.01-.005.048-.005.057v-.005Z"
        />
        <path fill="#FFF" d="M1931.174 39.388c-19.847 15.843-27.635-21.629-52.34-7.449l27.84 34.95" />
        <g fillRule="nonzero">
          <path
            fill="#00B35A"
            d="M1121.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
          />
          <path
            fill="#FFF"
            d="M1143.2 30.473a42.666 42.666 0 0 1-4.801 10.726c-6.675 10.654-17.205 17.023-24.868 20.482a77.008 77.008 0 0 1-6.856 2.704v.034c6.258-.12 11.901-1.1 16.817-2.915 5.576-2.064 10.115-5.174 13.498-9.226 4.968-5.957 7.123-13.597 6.23-21.805h-.02Zm-40.716 4.988c-4.261 5.327-5.709 13.244-4.419 24.213a36.09 36.09 0 0 0 .306 2.437l.048.034c4.644-2.275 22.747-12.604 22.665-35.108l-.043-.043c-8.657.913-14.734 3.684-18.561 8.467h.004Z"
          />
          <path
            fill="#FFF"
            d="m1123.411 26.646-.033.08c.22 3.761-.02 5.849-.717 9.337a40.52 40.52 0 0 1-8.122 17.28 47.63 47.63 0 0 1-5.92 6.202l.01.02c9.211-3.364 20.253-9.355 27.042-19.828 6.713-10.344 5.79-14.644 5.074-18.213l-.02-.005c-3.965 3.044-11.012 4.147-17.314 5.127Z"
          />
        </g>
        <path
          fill="#E22245"
          fillRule="nonzero"
          d="M2005.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
        />
        <path
          fill="#FFF"
          d="M2004.22 30.114c-2.786 0-5.82.292-8.82.87 3.034-4.405 7.86-7.253 13.225-7.253 3.97 0 7.93 1.687 10.597 4.205.482.454.85.492 1.218.076s.335-.831-.072-1.366c-3.483-4.573-8.6-7.65-14.634-7.53-7.893.158-14.6 5.805-17.157 13.617a31.581 31.581 0 0 0-6.378 3.01c-.521.334-.698.74-.521 1.232.191.459.559.621 1.08.459a61.624 61.624 0 0 1 5.036-1.405 17.556 17.556 0 0 0-.297 3.21c-.153 7.201 3.259 14.038 8.52 17.784.525.372.96.372 1.256-.081.296-.454.262-.87-.191-1.31-3.33-3.373-5.19-8.408-5.19-13.382 0-2.518.45-4.936 1.29-7.119 1.9-.188 3.807-.287 5.715-.296 13.488 0 24.864 5.016 24.864 11.615 0 3.334-3.746 6.665-8.19 8.9-1.08.536-1.184.908-1.041 1.443.153.497.626.655 1.185.454 7.788-2.876 13.674-7.606 13.674-13.34.024-7.787-11.094-13.793-25.17-13.793Zm6.058 36.01V50.63c0-.602-.55-.812-1.004-.54l-3.774 2.466v14.319h4.052a.733.733 0 0 0 .726-.746v-.005Zm-9.556.287V55.685c0-.397.177-.669.464-.846l2.842-1.715V66.89h-2.842a.465.465 0 0 1-.464-.483v.005Z"
        />
        <g fillRule="nonzero" transform="translate(1667.5)">
          <rect width={87} height={87} fill="#004384" rx={43.5} />
          <g fill="#FFF">
            <path d="M52.196 42.648a7.716 7.716 0 0 0 1.597-4.699c0-4.279-3.478-7.76-7.752-7.76H31.87v26.22h15.417c4.275 0 7.752-3.481 7.752-7.76a7.749 7.749 0 0 0-2.844-6.001Zm-13.893-7.636h6.466a2.94 2.94 0 0 1 2.936 2.937 2.94 2.94 0 0 1-2.936 2.939h-6.466v-5.876Zm7.046 16.574h-7.046V45.71h7.046a2.94 2.94 0 0 1 0 5.876Z" />
            <path d="M22.09 22.305V64.91h9.886v-3.216h-6.682V25.508h6.682v-3.203H22.09Zm32.68 0v3.204h6.681v36.186H54.77v3.215h9.886V22.305H54.77Z" />
          </g>
        </g>
        <g fillRule="nonzero">
          <path
            fill="url(#a)"
            d="M43 0c23.748 0 43 19.252 43 43S66.748 86 43 86 0 66.748 0 43 19.252 0 43 0Z"
            transform="translate(294.5)"
          />
          <path
            fill="#76B900"
            d="M314.69 40.344s5.295-8.084 15.643-8.882v-2.795c-11.466.927-21.5 11.084-21.5 11.084s5.734 16.087 21.5 17.582v-2.971c-11.571-1.467-15.642-14.018-15.642-14.018Zm15.643 8.408v2.719c-8.743-1.572-11.28-10.73-11.28-10.73s4.305-4.683 11.28-5.448v2.987c-3.66-.445-6.64 3-6.64 3s1.71 5.8 6.64 7.468v.004Zm0-24.863v4.778c.335-.024.564-.048.903-.058 13.034-.444 21.524 11.147 21.524 11.147S343.01 51.7 332.846 51.7a13.46 13.46 0 0 1-2.513-.229v2.89c.703.091 1.319.144 2.074.144 9.46 0 16.292-4.568 22.919-10.33 1.099.889 5.59 3.044 6.517 3.985-6.297 5.313-20.975 9.298-29.288 9.298-.803 0-1.467-.048-2.222-.125v4.778h35.834V23.89h-35.834Zm0 11.404v-3.831c.335-.024.56-.043.903-.048 9.374-.301 15.523 8.49 15.523 8.49s-6.64 9.293-13.76 9.293a7.871 7.871 0 0 1-2.666-.45V38.285c3.65.44 4.276 2.064 6.47 5.748l4.873-4.142s-3.555-4.706-9.56-4.706c-.655 0-1.171.047-1.783.11Z"
          />
        </g>
        <g fillRule="nonzero">
          <path fill="#E82127" d="M43.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43S.5 66.748.5 43s19.252-43 43-43Z" />
          <path
            fill="#FFF"
            d="m43.5 71.667 7.167-38.223c6.373 0 7.902 1.3 8.194 4.167 0 0 4.27-1.6 6.43-4.855-8.413-3.913-17.013-4.09-17.013-4.09L43.5 34.64l-4.778-5.972s-8.6.176-17.009 4.09c2.15 3.248 6.426 4.854 6.426 4.854.292-2.867 1.864-4.162 8.194-4.167L43.5 71.667Z"
          />
          <path
            fill="#FFF"
            d="M43.5 26.794a54.228 54.228 0 0 1 22.398 4.563c1.06-1.926 1.49-2.69 1.49-2.69-8.709-3.46-16.865-4.75-23.888-4.778-7.023.029-15.179 1.319-23.889 4.778 0 0 .545.955 1.496 2.69A54.223 54.223 0 0 1 43.5 26.789v.005Z"
          />
        </g>
        <g fillRule="nonzero">
          <path
            fill="#F0F3FA"
            d="M533.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
          />
          <path
            fill="#FFC107"
            d="M556.92 39.589H533.5v8.571h13.502c-1.973 5.561-7.262 9.173-13.502 9.173a14.333 14.333 0 1 1 9.508-25.035l6.756-6.756a23.774 23.774 0 0 0-16.264-6.43c-13.193 0-23.889 10.695-23.889 23.888 0 13.193 10.696 23.889 23.889 23.889S557.389 56.193 557.389 43c0-1.29-.163-1.897-.468-3.411Z"
          />
          <path
            fill="#FF3D00"
            d="m512.363 31.882 7.855 5.757a14.324 14.324 0 0 1 13.282-8.972c3.655 0 6.976 1.376 9.508 3.63l6.756-6.755a23.774 23.774 0 0 0-16.264-6.43 23.875 23.875 0 0 0-21.137 12.77Z"
          />
          <path
            fill="#4CAF50"
            d="M533.5 66.889a23.789 23.789 0 0 0 16.015-6.202l-7.391-6.259a14.238 14.238 0 0 1-8.624 2.905c-6.211 0-11.486-3.965-13.473-9.488l-7.793 6A23.87 23.87 0 0 0 533.5 66.89Z"
          />
          <path
            fill="#1976D2"
            d="M556.92 39.589H533.5v8.571h13.502c-.946 2.676-2.666 4.591-4.883 6.273l7.396 6.26c-.52.463 7.874-5.749 7.874-17.693 0-1.6-.163-1.897-.468-3.411Z"
          />
        </g>
        <g fillRule="nonzero">
          <path
            fill="#22275E"
            d="M1219.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
          />
          <path
            fill="#FFF"
            d="m1228.678 23.889-9.933 25.661-4.137-25.661c-.555 1.366-2.485 0-4.415 0h-14.305l-.277 1.094c3.311.817 5.8 1.634 8.008-1.094.688 4.166 1.261 4.778 1.61 7.372l7.171 30.85h10.206l16.005-38.222h-9.933Z"
          />
        </g>
        <g fillRule="nonzero">
          <path
            fill="url(#a)"
            d="M43 0c23.748 0 43 19.252 43 43S66.748 86 43 86 0 66.748 0 43 19.252 0 43 0Z"
            transform="translate(686.5)"
          />
          <path fill="#DB0916" d="M715.167 19.111h9.555V66.89h-9.555V19.11Zm19.11 0h9.556V66.89h-9.555V19.11Z" />
          <path fill="#FF3540" d="M734.278 66.889h9.555l-19.11-47.778h-9.556z" />
        </g>
        <g fillRule="nonzero">
          <path
            fill="url(#a)"
            d="M43 0c23.748 0 43 19.252 43 43S66.748 86 43 86 0 66.748 0 43 19.252 0 43 0Z"
            transform="translate(588.5)"
          />
          <path
            fill="#FFF"
            d="M650.611 50.63a23.077 23.077 0 0 1-3.24 6.15c-1.284 1.733-2.336 2.933-3.143 3.597-1.252 1.099-2.6 1.658-4.037 1.686-1.037 0-2.284-.282-3.736-.845a11.18 11.18 0 0 0-4.014-.841c-1.28 0-2.656.277-4.128.84-1.476.564-2.666.86-3.574.89-1.376.057-2.756-.517-4.128-1.73-.879-.722-1.973-1.969-3.287-3.727a25.27 25.27 0 0 1-3.478-6.545 22.823 22.823 0 0 1-1.457-7.788c0-2.881.66-5.37 1.973-7.454a11.581 11.581 0 0 1 9.718-5.456c1.099 0 2.537.325 4.324.956 1.782.635 2.924.955 3.426.955.377 0 1.648-.377 3.803-1.127 2.035-.693 3.76-.98 5.17-.87 3.817.296 6.683 1.72 8.59 4.29-3.412 1.96-5.103 4.707-5.07 8.228.034 2.742 1.08 5.031 3.144 6.842.937.84 1.983 1.49 3.144 1.954v-.005Zm-9.603-31.519c.028.287.048.573.048.86 0 2.15-.832 4.157-2.485 6.02-1.992 2.207-4.405 3.488-7.023 3.282a6.33 6.33 0 0 1-.048-.812c0-2.069.946-4.276 2.628-6.082.84-.917 1.91-1.672 3.206-2.284a9.986 9.986 0 0 1 3.674-.984Z"
          />
        </g>
        <g fillRule="nonzero">
          <path
            fill="#E40001"
            d="M141.5 0c23.748 0 43 19.252 43 43s-19.252 43-43 43-43-19.252-43-43 19.252-43 43-43Z"
          />
          <path
            fill="#FFF"
            d="M168.351 36.631c-4.95 0-8.734 3.034-8.734 8.314 0 4.462 3.44 7.61 8.065 7.61 3.512 0 6.426-2.995 7.262-4.887 0-.588-.501-.831-.97-.831-.802 1.457-3.076 2.57-4.013 2.532-5.59-.244-6.493-2.881-6.56-5.638-.105-3.005 2.642-5.604 4.988-5.408 1.367.124 1.84 2.761 3.039 3.702.373.244 1.242.206 1.81.086.436-.086 1.606-.396 1.606-1.576 0-.99-1.037-2.088-2.04-2.8-1.003-.55-3.249-1.104-4.448-1.104h-.005Zm-34.07 11.658c0 2.451-.54 3.75-2.246 3.87l.038.397c2.858-.039 5.758 0 8.538-.077l.034-.2c-1.29 0-2.28-1.582-2.28-3.55v-6.603c0-1.615 1.697-2.647 2.863-2.523 1.796.191 2.785 1.529 2.785 2.723v6.479c0 1.978-.511 3.201-1.835 3.316v.358h7.831v-.277c-1.166-.162-1.983-1.386-1.983-3.244v-6.794c0-1.457 1.1-2.446 3.034-2.485 1.701-.043 2.724 1.42 2.724 2.805v6.159c0 2.102-.545 3.397-1.902 3.559v.277c2.857.038 5.198.038 8.027 0v-.2c-1.5 0-2.007-1.147-2.007-3.55v-6.8c0-2.25-1.056-4.94-4.018-4.94-2.007 0-3.478 1.147-4.314 1.467-.583.273-.994.588-1.137.588-.263 0-.569-.191-.946-.669-.717-.879-2.007-1.385-3.369-1.385-1.739 0-3.473 1.146-4.29 1.466-.096.039-.712.392-.784.392-.129 0-.678 0-.678-.076 0-1.897.033-2.14.033-2.14h-1.123c-.334 0-.922.558-1.6.864-.812.401-2.451.358-3.125.564v1.338l1.768.396v2.609l-.038 5.886Z"
          />
          <path
            fill="#FFF"
            d="M126.579 52.283c3.024-.153 5.939-.898 5.939-4.204 0-2.408-2.337-3.928-4.797-5.136-1.586-.74-3.884-1.83-3.884-2.685 0-1.013 1.748-2.963 3.43-1.405l2.914 2.766 1.343-.31-.75-4.128c-.688.038-1.17.08-3.679-.392-3.091-.502-6.76 1.252-6.76 4.52 0 2.178 2.785 3.698 4.983 4.71 1.194.583 3.325 1.443 3.325 2.762 0 1.285-1.992 1.639-3.12 1.639-1.925 0-4.056-2.924-4.056-2.924h-1.262l.684 4.367c.239-.24 1.099-.2 1.275-.12.612.263 2.843.66 4.415.54Zm-7.272-13.234v-2.021l-3.736.038c-.478 0-.45-.12-.45-.817l.04-2.771-1.888-.034c-.583 1.477-1.304 2.451-1.71 2.647l-3.507 1.749v1.252l1.309.043c.587 0 .821-.043.821.74v8.209c0 2.494 1.2 4.323 4.296 4.4 2.322.076 3.736-1.094 4.73-2.179l-.277-1.05c-.416.31-.75.582-1.305.582-2.642 0-2.57-2.336-2.57-4.4V39.87c0-.822.062-.822.544-.822h3.703Z"
          />
        </g>
        <g fillRule="nonzero">
          <path
            fill="url(#a)"
            d="M43 0c23.748 0 43 19.252 43 43S66.748 86 43 86 0 66.748 0 43 19.252 0 43 0Z"
            transform="translate(392.5)"
          />
          <path
            fill="#FFF"
            d="M454.167 50.463c-1.3-1.644-2.303-2.819-2.303-5.977V33.999c0-4.425-.301-8.567-3.56-11.6-2.608-2.404-6.903-3.288-10.296-3.288-7.166 0-13.569 2.915-15.136 10.75-.133.889.521 1.266 1.042 1.395l6.794.621c.654 0 1.046-.63 1.175-1.261.526-2.656 2.876-4.888 5.485-4.888 1.433 0 4.262 1.443 4.262 4.477V34c-4.176 0-8.834.282-12.484 1.796-4.3 1.773-7.98 5.17-7.98 10.607 0 6.951 5.352 10.75 11.085 10.75 4.826 0 7.453-1.252 11.228-4.921 1.18 1.767 1.906 3.029 4.123 4.92.526.259 1.175.259 1.562-.119 1.3-1.146 3.574-3.535 5.003-4.672.654-.512.525-1.266 0-1.897ZM441.63 40.61c0 2.528-.492 8.834-6.88 8.834-3.282 0-3.35-3.87-3.35-3.87 0-4.969 5.275-6.622 10.23-6.622v1.658Z"
          />
          <path
            fill="#FFB300"
            d="M455.199 61.619c-5.304 3.263-13.24 5.27-19.675 5.27a39.316 39.316 0 0 1-23.703-7.802c-.506-.42 0-.841.507-.63a52.723 52.723 0 0 0 23.067 5.16c5.805 0 13.12-1.052 19.044-3.159.88-.21 1.639.63.76 1.161Zm1.892-2.217c-.63-.736-4.415-.315-6.054-.21-.51 0-.63-.315-.129-.63 2.9-1.797 7.817-1.267 8.323-.636.502.635-.129 4.744-2.9 6.64-.382.321-.884.106-.63-.205.63-1.27 2.02-4.218 1.385-4.959h.005Z"
          />
        </g>
        <g transform="translate(1274.5)">
          <rect width={86} height={86} fill="#003662" rx={43} />
          <g fill="#FEFEFE">
            <path d="M70.103 59.38c-9.226-2.97-17.087-3.118-22.922-2.516 2.053-4.814 2.262-9.89 1.378-14.828C34.274 31.862 18.53 22.867 3.392 16.448c11.107 7.52 23.354 29.218 28.494 52.49 10.946-3.243 27.811-5.479 49.547 1.838-3.418-3.817-7.226-7.634-11.33-11.396ZM70.116 59.386c3.937 1.264 8.11 3.037 12.493 5.484-10.753-17.764-29.99-37.112-48.866-49.646 4.867 4.816 12.763 15.418 14.81 26.788 7.733 5.51 15.03 11.374 21.563 17.374Z" />
          </g>
        </g>
        <g transform="translate(1766.5)">
          <rect width={86} height={86} fill="#373435" rx={43} />
          <g fill="#FEFEFE" fillRule="nonzero">
            <path d="M11.473 19.959a5.655 5.655 0 0 1 5.656-5.656h51.742a5.655 5.655 0 0 1 5.656 5.656v23.994c-2.311-3.343-6.256-5.634-10.239-5.634-3.04 0-6.313 1.68-7.325 3.085l-1.246-2.799H45.28l-4.042 5.597-3.753-5.286h-9.039l8.572 11.68-8.572 11.994h8.572l4.22-6.064 4.664 6.53h6.084v8.642H17.129a5.656 5.656 0 0 1-5.656-5.657V19.96Zm63.054 37.293v8.79a5.656 5.656 0 0 1-5.656 5.656h-9.399v-10.04c1.394 1.27 2.986 1.554 4.816 1.554 5.052 0 8.345-2.763 10.239-5.96Z" />
            <path d="M52.24 42.133v16.05l-6.466-7.62zM59.503 50.714c0-2.911 2.1-5.273 4.686-5.273 2.587 0 4.684 2.362 4.684 5.273 0 2.91-2.097 5.274-4.684 5.274s-4.686-2.364-4.686-5.274Z" />
          </g>
        </g>
        <path
          fill="#001E62"
          d="M1514.5 86c23.748 0 43-19.252 43-43s-19.252-43-43-43-43 19.252-43 43 19.252 43 43 43Z"
        />
        <path
          fill="#FEFEFE"
          d="m1529.138 41.066.747-3.64h-6.434v-4.973l-4.519.681V47.99c0 5.136 2.016 6.664 7.509 6.664h2.406l.747-3.61h-2.83c-2.502 0-3.313-.617-3.313-3.347v-6.63h5.687Zm-31.042-10.338V46.69c0 5.917 2.73 8.386 8.68 8.386 6.174 0 8.906-2.406 8.906-8.353v-1.528c0-5.59-2.536-8.322-8.062-8.322-1.755 0-3.674.36-4.973 1.367v-8.225l-4.55.714Zm8.84 9.72c3.12 0 4.228 1.236 4.228 4.91v1.364c0 3.575-1.107 4.81-4.292 4.81-3.186 0-4.29-1.235-4.29-4.843v-1.852c0-3.284 1.104-4.388 4.354-4.388Zm33.124-3.576c-5.948 0-8.68 2.503-8.68 8.225v.878c0 5.427 2.375 8.06 7.965 8.06 1.789 0 3.575-.325 4.843-1.332v.65c0 3.056-1.332 4.064-4.453 4.064h-1.867c-1.07.018-1.555.207-2.199 1.261h-.002a29.288 29.288 0 0 1-24.805 13.663c-16.18 0-29.344-13.162-29.344-29.341 0-16.179 13.165-29.344 29.344-29.344 11.363 0 21.702 6.583 26.54 16.825h1.348a30.598 30.598 0 0 0-27.888-18.046c-16.854 0-30.566 13.711-30.566 30.565s13.712 30.565 30.566 30.565a30.499 30.499 0 0 0 24.76-12.64h4.374c5.753 0 8.71-2.176 8.71-7.735v-8.386c0-5.526-2.827-7.932-8.646-7.932Zm4.16 9.654c0 3.056-1.071 3.998-4.16 3.998-3.478 0-4.161-1.202-4.161-4.746v-.55c0-3.511.617-4.78 4.16-4.78 3.478 0 4.162 1.236 4.162 4.81v1.268Z"
        />
        <g fillRule="nonzero" transform="translate(882.5)">
          <rect width={86} height={86} fill="#283B8E" rx={43} />
          <path
            fill="#FEFEFE"
            d="M51.24 37.833c0 5.881-5.025 10.651-11.224 10.651-6.196 0-11.224-4.77-11.224-10.65 0-2.973 1.286-5.659 3.354-7.593.946-.986 2.128-1.738 3.524-2.231A11.763 11.763 0 0 1 40 27.183h.02c6.198 0 11.224 4.77 11.224 10.65h-.002Zm6.956 0c0-9.305-7.767-16.886-17.487-17.235-.163-.007-.33-.01-.496-.01-.064-.003-.13-.008-.197-.008h-.033c-4.857 0-9.281 1.617-12.448 4.552a15.48 15.48 0 0 0-2.123 2.43c-2.245 2.867-3.575 6.421-3.575 10.271 0 9.524 8.14 17.249 18.18 17.249 2.65 0 10.756-.706 10.756 7.713 0 7.235-8.64 7.798-11.171 7.798-5.151 0-10.655-2.049-10.655-7.798v-.604c0-.344-.291-.62-.65-.62l-5.81-.002a.635.635 0 0 0-.65.622v.604c0 4.406 1.837 8.126 5.317 10.76 3.164 2.398 7.583 3.715 12.448 3.715 10.832 0 18.214-5.68 18.214-14.475 0-4.882-2.322-8.81-6.62-11.365 4.259-3.155 7-8.07 7-13.597Z"
          />
          <path
            fill="#4B9ED7"
            d="M49.684 11.564c0 1.565 1.338 2.833 2.988 2.833h4.238c.706 0 1.281.543 1.281 1.213v4.024c0 1.566 1.336 2.836 2.986 2.836 1.65 0 2.988-1.27 2.988-2.836v-9.488c0-.783-.67-1.416-1.493-1.416h-10c-1.65 0-2.988 1.27-2.988 2.834Z"
          />
        </g>
        <g fillRule="nonzero" transform="translate(1372.5)">
          <rect width={87} height={87} fill="#005DA4" rx={43.5} />
          <g fill="#FFF">
            <path d="M14.494 23.11h-.22v32.298h22.824v-23.23h-5.254v17.919h-3.529V31.96H23.06v18.137h-3.307V28.42h52.92v-5.31z" />
            <path d="M58.581 32.177V55.41h8.6v3.758H14.273v4.864h58.403V32.177H58.58Zm5.277 5.536h3.323v12.606h-3.323V37.713Z" />
            <path d="M40.51 32.177V55.41h14.757V50.32h-9.481v-3.54h9.699V32.177H40.509Zm5.276 5.309h4.195v4.211h-4.195v-4.211Z" />
          </g>
        </g>
      </g>
    </svg>
  );
}

function FundamenteiSignature(props: SVGProps<SVGSVGElement>) {
  const intl = useIntl();
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={216} height={38} {...props} viewBox="0 0 216 38">
      <title>{intl.formatMessage({ defaultMessage: "Assinatura Fundamentei", id: "kzRerl" })}</title>
      <g fill="currentColor" fillRule="nonzero" stroke="currentColor" strokeWidth={0.75}>
        <path d="M29.312 2.2c-.672-.032-1.76-.04-3.264-.024a217.16 217.16 0 0 0-10.944.408c-2.048.128-3.976.272-5.784.432-1.808.16-3.384.344-4.728.552-1.344.208-2.256.424-2.736.648-.16.096-.144.208.048.336.192.128.448.248.768.36.32.112.632.216.936.312.304.096.488.144.552.144.512.128 1.288.32 2.328.576 1.04.256 2.112.552 3.216.888 1.104.336 2.128.688 3.072 1.056.944.368 1.592.696 1.944.984.288.256.384.512.288.768-.096.256-.336.32-.72.192a3.896 3.896 0 0 1-.672-.264 12.61 12.61 0 0 0-.672-.312 21.356 21.356 0 0 0-2.736-.96c-.928-.256-1.856-.504-2.784-.744A60.496 60.496 0 0 1 4.64 6.76a24.47 24.47 0 0 1-2.688-1.008 2.79 2.79 0 0 1-1.08-.744C.568 4.672.496 4.28.656 3.832c.128-.352.656-.656 1.584-.912s2.12-.488 3.576-.696a69.254 69.254 0 0 1 4.944-.504c1.84-.128 3.744-.24 5.712-.336 1.968-.096 3.928-.16 5.88-.192s3.76-.056 5.424-.072a459.51 459.51 0 0 1 4.344-.024c1.232 0 2.12.016 2.664.048.736.032 1.696.08 2.88.144 1.184.064 2.408.144 3.672.24a99.53 99.53 0 0 1 3.6.336c1.136.128 2.04.288 2.712.48.16.032.304.096.432.192a.614.614 0 0 1 .24.384c.032.256-.032.424-.192.504-.16.08-.36.112-.6.096a5.55 5.55 0 0 1-.72-.096c-.24-.048-.424-.088-.552-.12a95.126 95.126 0 0 0-7.824-.84 117.634 117.634 0 0 0-7.776-.264v.048a620.443 620.443 0 0 1-5.832 9.432 226.737 226.737 0 0 0-5.688 9.528 77.808 77.808 0 0 1 10.944-.768c.16 0 .288.064.384.192a.825.825 0 0 1 .168.408.536.536 0 0 1-.096.384c-.08.112-.2.168-.36.168-2.016 0-4 .088-5.952.264-1.952.176-3.92.392-5.904.648-1.344 2.304-2.68 4.6-4.008 6.888a564.103 564.103 0 0 0-3.96 6.936c.512.256.68.504.504.744-.176.24-.448.408-.816.504-.368.096-.72.08-1.056-.048-.336-.128-.424-.432-.264-.912.16-.48.432-1.064.816-1.752a88.6 88.6 0 0 1 2.592-4.344 81.89 81.89 0 0 0 1.104-1.776c.576-1.024 1.168-2.04 1.776-3.048.608-1.008 1.2-2.024 1.776-3.048-1.216.224-2.44.456-3.672.696a93.53 93.53 0 0 0-3.672.792c-.608.16-1.24.344-1.896.552a9.61 9.61 0 0 1-1.896.408c-.384.032-.632-.088-.744-.36-.112-.272.008-.488.36-.648.096-.064.392-.168.888-.312s1.032-.296 1.608-.456c.576-.16 1.136-.304 1.68-.432a21.05 21.05 0 0 1 1.104-.24 64.74 64.74 0 0 1 6.96-1.248c1.984-3.2 3.944-6.4 5.88-9.6 1.936-3.2 3.912-6.4 5.928-9.6ZM45.392 23.176c-.384.384-.984.992-1.8 1.824a65.683 65.683 0 0 1-3.048 2.88c-1.472 1.312-2.736 2.168-3.792 2.568-1.056.4-1.832.432-2.328.096-.496-.336-.704-1.024-.624-2.064.08-1.04.52-2.36 1.32-3.96.64-1.312 1.256-2.376 1.848-3.192.592-.816 1.08-1.432 1.464-1.848.288-.288.576-.288.864 0 .128.128.176.256.144.384s-.08.256-.144.384A89.762 89.762 0 0 0 37.64 22.6c-.432.64-.888 1.424-1.368 2.352-.64 1.216-1.032 2.2-1.176 2.952-.144.752-.072 1.24.216 1.464.288.224.808.144 1.56-.24s1.736-1.12 2.952-2.208c.8-.704 1.56-1.408 2.28-2.112a74.928 74.928 0 0 0 1.968-1.992c.592-.624 1.12-1.176 1.584-1.656.464-.48.824-.864 1.08-1.152.192-.192.424-.224.696-.096.272.128.376.336.312.624-.032.16-.12.4-.264.72-.144.32-.32.696-.528 1.128-.208.432-.424.912-.648 1.44-.224.528-.448 1.048-.672 1.56-.48 1.184-.776 2.12-.888 2.808-.112.688-.032 1.176.24 1.464.272.288.76.368 1.464.24s1.632-.416 2.784-.864a31.95 31.95 0 0 0 3.792-1.872c1.28-.736 2.288-1.456 3.024-2.16.288-.288.528-.296.72-.024.192.272.16.552-.096.84-.128.16-.416.392-.864.696-.448.304-.992.656-1.632 1.056-.64.4-1.36.808-2.16 1.224-.8.416-1.6.8-2.4 1.152-2.752 1.216-4.56 1.504-5.424.864-.864-.64-.912-2.144-.144-4.512.192-.608.424-1.184.696-1.728.272-.544.488-1.008.648-1.392Z" />
        <path d="M66.272 24.616c.256-1.216.272-2.024.048-2.424-.224-.4-.592-.544-1.104-.432-.512.112-1.112.408-1.8.888-.688.48-1.368.976-2.04 1.488-.64.48-1.28 1.016-1.92 1.608l-1.872 1.728c-.608.56-1.168 1.08-1.68 1.56s-.96.848-1.344 1.104c-.288.192-.56.208-.816.048-.256-.16-.304-.416-.144-.768a58.744 58.744 0 0 1 2.448-4.56 60.735 60.735 0 0 1 2.832-4.368c.192-.256.448-.304.768-.144s.384.4.192.72a51.62 51.62 0 0 1-1.824 3 51.62 51.62 0 0 0-1.824 3c.32-.256.752-.64 1.296-1.152a70.642 70.642 0 0 1 1.8-1.632 38.448 38.448 0 0 1 2.088-1.704 18.21 18.21 0 0 1 2.136-1.416c.688-.384 1.336-.648 1.944-.792.608-.144 1.136-.088 1.584.168.512.32.776.704.792 1.152.016.448-.024.928-.12 1.44-.096.672-.328 1.448-.696 2.328-.368.88-.624 1.664-.768 2.352-.144.688-.072 1.16.216 1.416s1.008.096 2.16-.48a37.34 37.34 0 0 0 3.168-1.824c.992-.64 2-1.312 3.024-2.016.384-.224.672-.2.864.072.192.272.16.52-.096.744-.256.224-.632.512-1.128.864a43.23 43.23 0 0 1-1.68 1.128c-.624.4-1.28.8-1.968 1.2-.688.4-1.352.744-1.992 1.032a9.905 9.905 0 0 1-1.776.624c-.544.128-.976.144-1.296.048-.736-.256-1.072-.664-1.008-1.224.064-.56.176-1.16.336-1.8l1.2-2.976Z" />
        <path d="M81.152 30.136c.288-.544.648-1.272 1.08-2.184.432-.912.888-1.864 1.368-2.856.48-.992.968-1.96 1.464-2.904s.936-1.736 1.32-2.376c-.416.608-.936 1.352-1.56 2.232-.624.88-1.304 1.8-2.04 2.76a76.699 76.699 0 0 1-2.28 2.832 26.202 26.202 0 0 1-2.304 2.424c-.752.688-1.456 1.192-2.112 1.512-.656.32-1.208.368-1.656.144-.352-.16-.592-.456-.72-.888a4.315 4.315 0 0 1-.192-1.224c.032-.832.2-1.736.504-2.712.304-.976.712-1.888 1.224-2.736A8.598 8.598 0 0 1 77.072 22c.704-.592 1.472-.936 2.304-1.032.512-.064 1.088.032 1.728.288.64.256 1.088.656 1.344 1.2.192.32.16.552-.096.696-.256.144-.496.104-.72-.12-.48-.48-.912-.784-1.296-.912s-.912-.096-1.584.096c-.896.256-1.632.864-2.208 1.824a14.81 14.81 0 0 0-1.392 3.072c-.192.864-.304 1.6-.336 2.208-.032.608.064 1.008.288 1.2.224.192.584.152 1.08-.12.496-.272 1.176-.856 2.04-1.752.832-.832 1.72-1.856 2.664-3.072a128.132 128.132 0 0 0 2.88-3.864c.976-1.36 1.936-2.728 2.88-4.104a658.68 658.68 0 0 0 2.616-3.84 121.59 121.59 0 0 1 2.136-3.072c.624-.864 1.096-1.456 1.416-1.776.224-.192.456-.208.696-.048.24.16.296.4.168.72-.448.992-1 1.984-1.656 2.976a50.061 50.061 0 0 0-1.8 2.928 169.034 169.034 0 0 0-2.976 5.232c-.96 1.76-1.904 3.552-2.832 5.376l-3.648 7.248c-.256.544-.528 1.136-.816 1.776-.288.64-.592 1.232-.912 1.776-.096.16-.2.256-.312.288a.324.324 0 0 1-.312-.072.721.721 0 0 1-.216-.336.646.646 0 0 1 .024-.456c.096-.416.264-.896.504-1.44a41.366 41.366 0 0 1 1.68-3.36c.304-.544.552-1.008.744-1.392ZM97.904 26.152c-.288.832-.408 1.48-.36 1.944.048.464.224.776.528.936.304.16.712.208 1.224.144a8.5 8.5 0 0 0 1.728-.432c.704-.224 1.456-.52 2.256-.888.8-.368 1.552-.736 2.256-1.104a17.768 17.768 0 0 0 1.824-1.08c.512-.352.848-.624 1.008-.816.128-.16.264-.232.408-.216a.696.696 0 0 1 .384.168.558.558 0 0 1 .192.384c.016.16-.04.32-.168.48-.224.256-.6.568-1.128.936-.528.368-1.144.736-1.848 1.104-.704.368-1.464.728-2.28 1.08-.816.352-1.608.656-2.376.912-.768.256-1.488.448-2.16.576-.672.128-1.216.144-1.632.048-.544-.096-.904-.392-1.08-.888-.176-.496-.24-1.048-.192-1.656.048-.608.16-1.208.336-1.8.176-.592.344-1.064.504-1.416l-3.936 3.408c-1.728 1.504-3.048 2.416-3.96 2.736-.912.32-1.48.248-1.704-.216-.224-.464-.184-1.192.12-2.184.304-.992.808-2.056 1.512-3.192a22.46 22.46 0 0 1 2.472-3.264c.944-1.04 1.944-1.816 3-2.328a8.85 8.85 0 0 1 1.44-.504 6.537 6.537 0 0 1 1.68-.216c.576 0 1.12.08 1.632.24.512.16.928.448 1.248.864.224.288.208.568-.048.84s-.544.296-.864.072c-.928-.64-1.832-.928-2.712-.864-.88.064-1.784.416-2.712 1.056a11.94 11.94 0 0 0-2.088 1.896 17.841 17.841 0 0 0-1.608 2.184 41.69 41.69 0 0 0-1.272 2.136c-.4.72-.616 1.272-.648 1.656-.032.384.2.512.696.384.496-.128 1.4-.72 2.712-1.776.672-.544 1.36-1.12 2.064-1.728a44.903 44.903 0 0 0 3.48-3.336c.432-.464.728-.808.888-1.032a.571.571 0 0 1 .384-.288.612.612 0 0 1 .72.36.565.565 0 0 1 0 .456c-.256.64-.576 1.32-.96 2.04s-.704 1.448-.96 2.184Z" />
        <path d="M114.8 22.792c.448-1.12.504-1.712.168-1.776-.336-.064-.872.288-1.608 1.056a33.91 33.91 0 0 0-1.896 2.16 75.078 75.078 0 0 1-1.848 2.208 68.854 68.854 0 0 0-1.656 2.304c-.528.768-1.064 1.536-1.608 2.304a.509.509 0 0 1-.36.216.717.717 0 0 1-.408-.072.517.517 0 0 1-.264-.288.585.585 0 0 1 .024-.432 130.089 130.089 0 0 1 2.688-4.992 52.568 52.568 0 0 1 3.024-4.752c.192-.256.456-.312.792-.168s.424.392.264.744c-.096.128-.248.352-.456.672a45.751 45.751 0 0 1-1.416 2.04l-.48.648c.224-.32.536-.712.936-1.176.4-.464.824-.928 1.272-1.392.448-.464.888-.904 1.32-1.32.432-.416.824-.736 1.176-.96.224-.128.44-.224.648-.288.208-.064.44-.016.696.144.32.16.504.456.552.888.048.432.024.896-.072 1.392-.096.496-.224.984-.384 1.464s-.288.832-.384 1.056l-.192.384c-.096.192-.184.4-.264.624l-.216.6c-.064.176-.096.296-.096.36.16-.128.472-.384.936-.768.464-.384 1.008-.824 1.632-1.32a146.74 146.74 0 0 1 1.968-1.536 77.102 77.102 0 0 1 1.968-1.464l1.608-1.152c.448-.32.752-.496.912-.528a1.78 1.78 0 0 1 1.224-.048c.432.128.664.448.696.96 0 .128-.056.36-.168.696-.112.336-.264.744-.456 1.224s-.408.992-.648 1.536l-.696 1.584c-.16.384-.328.792-.504 1.224-.176.432-.264.8-.264 1.104 0 .304.144.52.432.648.288.128.832.08 1.632-.144a21.32 21.32 0 0 0 2.28-.816 49.752 49.752 0 0 0 2.424-1.08 61.845 61.845 0 0 0 2.064-1.032c.608-.32 1.008-.56 1.2-.72.128-.128.256-.176.384-.144a.533.533 0 0 1 .312.216c.08.112.12.248.12.408 0 .16-.048.304-.144.432-.224.224-.632.496-1.224.816-.592.32-1.272.648-2.04.984-.768.336-1.568.672-2.4 1.008-.832.336-1.584.616-2.256.84-.48.16-1.016.32-1.608.48-.592.16-1.104.176-1.536.048-.432-.128-.712-.448-.84-.96s.032-1.344.48-2.496c.224-.544.448-1.112.672-1.704.224-.592.464-1.16.72-1.704.256-.64.464-1.152.624-1.536.16-.384.208-.632.144-.744-.064-.112-.256-.08-.576.096-.32.176-.816.52-1.488 1.032-.64.48-1.328 1.032-2.064 1.656-.736.624-1.448 1.216-2.136 1.776a54.254 54.254 0 0 1-1.872 1.464c-.56.416-.952.672-1.176.768-.192.064-.424.072-.696.024-.272-.048-.44-.184-.504-.408-.16-.192-.232-.448-.216-.768.016-.32.08-.68.192-1.08s.256-.816.432-1.248c.176-.432.344-.856.504-1.272Z" />
        <path d="M132.8 27.448c-.16.288-.176.616-.048.984s.32.632.576.792c.448.288.928.48 1.44.576.512.096 1.04.128 1.584.096.864-.064 1.936-.272 3.216-.624A43.969 43.969 0 0 0 143.456 28a58.615 58.615 0 0 0 3.792-1.584c1.216-.56 2.208-1.096 2.976-1.608a3.11 3.11 0 0 1 .408-.168c.144-.048.264.008.36.168.096.128.136.28.12.456-.016.176-.12.328-.312.456-.992.704-2.16 1.352-3.504 1.944a43.609 43.609 0 0 1-4.08 1.56c-.384.128-.92.312-1.608.552-.688.24-1.432.464-2.232.672-.8.208-1.624.376-2.472.504-.848.128-1.632.144-2.352.048-.72-.096-1.352-.32-1.896-.672-.544-.352-.896-.912-1.056-1.68-.16-.704-.024-1.464.408-2.28.432-.816 1.04-1.608 1.824-2.376a17.34 17.34 0 0 1 2.688-2.136 20.067 20.067 0 0 1 2.952-1.608c.96-.416 1.848-.664 2.664-.744.816-.08 1.416.072 1.8.456.352.352.496.712.432 1.08-.064.368-.24.728-.528 1.08a6.214 6.214 0 0 1-.984.96c-.368.288-.68.512-.936.672-.512.352-1.16.752-1.944 1.2-.784.448-1.608.864-2.472 1.248-.864.384-1.712.704-2.544.96-.832.256-1.552.352-2.16.288Zm.72-1.248a17.124 17.124 0 0 0 2.4-.648 26.932 26.932 0 0 0 2.688-1.08 19.574 19.574 0 0 0 2.544-1.416c.8-.528 1.456-1.112 1.968-1.752.288-.384.304-.6.048-.648-.256-.048-.592-.024-1.008.072a10.74 10.74 0 0 0-1.224.36c-.4.144-.616.216-.648.216a13.35 13.35 0 0 0-1.848.792 19.42 19.42 0 0 0-1.92 1.128c-.624.416-1.2.88-1.728 1.392a7.573 7.573 0 0 0-1.272 1.584Z" />
        <path d="M160.496 24.616c.256-1.216.272-2.024.048-2.424-.224-.4-.592-.544-1.104-.432-.512.112-1.112.408-1.8.888-.688.48-1.368.976-2.04 1.488-.64.48-1.28 1.016-1.92 1.608l-1.872 1.728c-.608.56-1.168 1.08-1.68 1.56s-.96.848-1.344 1.104c-.288.192-.56.208-.816.048-.256-.16-.304-.416-.144-.768a58.744 58.744 0 0 1 2.448-4.56 60.735 60.735 0 0 1 2.832-4.368c.192-.256.448-.304.768-.144s.384.4.192.72a51.62 51.62 0 0 1-1.824 3 51.62 51.62 0 0 0-1.824 3c.32-.256.752-.64 1.296-1.152a70.642 70.642 0 0 1 1.8-1.632 38.448 38.448 0 0 1 2.088-1.704 18.21 18.21 0 0 1 2.136-1.416c.688-.384 1.336-.648 1.944-.792.608-.144 1.136-.088 1.584.168.512.32.776.704.792 1.152.016.448-.024.928-.12 1.44-.096.672-.328 1.448-.696 2.328-.368.88-.624 1.664-.768 2.352-.144.688-.072 1.16.216 1.416s1.008.096 2.16-.48a37.34 37.34 0 0 0 3.168-1.824c.992-.64 2-1.312 3.024-2.016.384-.224.672-.2.864.072.192.272.16.52-.096.744-.256.224-.632.512-1.128.864a43.23 43.23 0 0 1-1.68 1.128c-.624.4-1.28.8-1.968 1.2-.688.4-1.352.744-1.992 1.032a9.905 9.905 0 0 1-1.776.624c-.544.128-.976.144-1.296.048-.736-.256-1.072-.664-1.008-1.224.064-.56.176-1.16.336-1.8l1.2-2.976Z" />
        <path d="M176 13.816c-2.016 0-4 .008-5.952.024s-3.936.056-5.952.12c-.896.032-1.84.104-2.832.216a14.99 14.99 0 0 1-2.784.072c-.352-.032-.528-.208-.528-.528 0-.32.176-.512.528-.576.928-.192 1.944-.28 3.048-.264 1.104.016 2.12-.008 3.048-.072 2.016-.096 4.04-.152 6.072-.168 2.032-.016 4.056-.024 6.072-.024.128-.192.368-.544.72-1.056s.736-1.032 1.152-1.56c.416-.528.816-.992 1.2-1.392.384-.4.672-.568.864-.504a.727.727 0 0 1 .264.384.71.71 0 0 1-.024.48c-.096.224-.264.496-.504.816a81.995 81.995 0 0 1-1.632 2.088c-.272.336-.488.616-.648.84 1.216 0 2.504-.016 3.864-.048 1.36-.032 2.632.048 3.816.24.352.064.528.264.528.6 0 .336-.176.536-.528.6-.448.064-1.04.08-1.776.048s-1.512-.072-2.328-.12l-2.4-.144a32.92 32.92 0 0 0-1.944-.072 95.823 95.823 0 0 1-1.296 2.016 326.28 326.28 0 0 0-2.016 3.12A114.654 114.654 0 0 0 171.8 22.6a66.507 66.507 0 0 0-1.968 3.576c-.56 1.104-.968 2.08-1.224 2.928-.256.848-.24 1.416.048 1.704.256.256.768.36 1.536.312.768-.048 1.76-.248 2.976-.6 1.152-.32 2.4-.752 3.744-1.296a66.356 66.356 0 0 0 3.816-1.68 71.03 71.03 0 0 0 3.168-1.608c.912-.496 1.512-.84 1.8-1.032.128-.096.264-.16.408-.192.144-.032.28.032.408.192.288.192.288.432 0 .72-.288.256-.912.664-1.872 1.224-.96.56-2.096 1.144-3.408 1.752a70.69 70.69 0 0 1-4.2 1.776 34.06 34.06 0 0 1-4.2 1.344c-1.312.32-2.456.464-3.432.432-.976-.032-1.624-.336-1.944-.912-.16-.256-.248-.592-.264-1.008a4.204 4.204 0 0 1 .072-1.056c.096-.448.336-1.08.72-1.896.384-.816.848-1.72 1.392-2.712.544-.992 1.136-2.032 1.776-3.12.64-1.088 1.272-2.128 1.896-3.12.624-.992 1.192-1.88 1.704-2.664.512-.784.928-1.4 1.248-1.848Z" />
        <path d="M185.552 27.448c-.16.288-.176.616-.048.984s.32.632.576.792c.448.288.928.48 1.44.576.512.096 1.04.128 1.584.096.864-.064 1.936-.272 3.216-.624A43.969 43.969 0 0 0 196.208 28 58.615 58.615 0 0 0 200 26.416c1.216-.56 2.208-1.096 2.976-1.608a3.11 3.11 0 0 1 .408-.168c.144-.048.264.008.36.168.096.128.136.28.12.456-.016.176-.12.328-.312.456-.992.704-2.16 1.352-3.504 1.944a43.609 43.609 0 0 1-4.08 1.56c-.384.128-.92.312-1.608.552-.688.24-1.432.464-2.232.672-.8.208-1.624.376-2.472.504-.848.128-1.632.144-2.352.048-.72-.096-1.352-.32-1.896-.672-.544-.352-.896-.912-1.056-1.68-.16-.704-.024-1.464.408-2.28.432-.816 1.04-1.608 1.824-2.376a17.34 17.34 0 0 1 2.688-2.136 20.067 20.067 0 0 1 2.952-1.608c.96-.416 1.848-.664 2.664-.744.816-.08 1.416.072 1.8.456.352.352.496.712.432 1.08-.064.368-.24.728-.528 1.08a6.214 6.214 0 0 1-.984.96c-.368.288-.68.512-.936.672-.512.352-1.16.752-1.944 1.2-.784.448-1.608.864-2.472 1.248-.864.384-1.712.704-2.544.96-.832.256-1.552.352-2.16.288Zm.72-1.248a17.124 17.124 0 0 0 2.4-.648 26.932 26.932 0 0 0 2.688-1.08 19.574 19.574 0 0 0 2.544-1.416c.8-.528 1.456-1.112 1.968-1.752.288-.384.304-.6.048-.648-.256-.048-.592-.024-1.008.072a10.74 10.74 0 0 0-1.224.36c-.4.144-.616.216-.648.216a13.35 13.35 0 0 0-1.848.792 19.42 19.42 0 0 0-1.92 1.128c-.624.416-1.2.88-1.728 1.392a7.573 7.573 0 0 0-1.272 1.584Z" />
        <path d="M215.12 24.952c.128.128.192.28.192.456a.623.623 0 0 1-.192.456c-.448.448-1.232 1-2.352 1.656a35.26 35.26 0 0 1-3.648 1.848 30.698 30.698 0 0 1-3.792 1.392c-1.216.352-2.112.448-2.688.288-.48-.128-.792-.36-.936-.696a2.297 2.297 0 0 1-.168-1.104c.032-.4.12-.816.264-1.248.144-.432.28-.792.408-1.08.16-.416.328-.832.504-1.248.176-.416.376-.816.6-1.2.416-.8.864-1.544 1.344-2.232l1.488-2.136c.128-.192.264-.296.408-.312a.46.46 0 0 1 .36.096c.096.08.168.2.216.36a.57.57 0 0 1-.024.432 15.497 15.497 0 0 1-1.416 2.208 17.466 17.466 0 0 0-1.464 2.256c-.096.192-.232.488-.408.888-.176.4-.352.816-.528 1.248-.176.432-.328.84-.456 1.224s-.192.672-.192.864c0 .128.008.232.024.312.016.08.088.152.216.216.32.16.784.176 1.392.048a12.958 12.958 0 0 0 2.064-.648 25.97 25.97 0 0 0 2.4-1.104 102.32 102.32 0 0 0 2.376-1.272c.752-.416 1.416-.8 1.992-1.152a17.42 17.42 0 0 0 1.248-.816c.128-.064.256-.112.384-.144.128-.032.256.016.384.144Zm-5.328-8.4-.288.432c-.064.096-.12.176-.168.24a2.625 2.625 0 0 0-.168.264 3.499 3.499 0 0 1-.336.456c-.16.192-.328.344-.504.456-.176.112-.344.12-.504.024-.224-.128-.312-.312-.264-.552.048-.24.168-.496.36-.768a12.537 12.537 0 0 1 1.104-1.32.457.457 0 0 1 .432-.144c.16.032.288.104.384.216a.544.544 0 0 1 .144.36c0 .128-.064.24-.192.336Z" />
      </g>
    </svg>
  );
}
